"use client";

import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  BarChart3,
  Users,
  Award,
  Eye,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  HelpCircle,
  CheckCircle,
  Info,
} from "lucide-react";
import "./AnalysisComparisonPage.css";
import BackButton from "../shared/backButton";
import CustomTooltip from "../shared/CustomTooltip.js";
import { getAllPeopleApi } from "../../services/peopleService";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { getAnalysisQuestions } from "./get-analysis-questions.js";
import { getAllTeamsApi } from "../../services/teamsService";
import { getDepartmentsListApi } from "../../services/settingsService";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import { getGenerateComparisonAnalysis } from "../../services/reportsService";
// Import reports
import {
  getAllJobRolesApi,
  getJobRolesDropDownApi,
} from "../../services/jobRolesService";
import SkillGapAnalysisReport from "./short-reports/skill-gap-analysis";
import PersonalityDistributionReport from "./short-reports/personality-distribution";
import LeadershipMetricsReport from "./short-reports/leadership-metrics";
import InternalMobilityReport from "./short-reports/internal-mobility-metrics-report";
import AggregateSkillGapsReport from "./short-reports/aggregate-skill-gaps-report";
import UpskillingReadinessReport from "./short-reports/upskilling-readiness-report";

// ADD with the other short-report imports
import UserSkillGapDeltaReport from "./short-reports/user-skill-gap-delta";
import UserMobilityReadinessReport from "./short-reports/user-mobility-readiness";
import UserLeadershipPotentialReport from "./short-reports/user-leadership-potential";

// ADD with the other short-report imports (near existing short-report imports)
import UserTeamSoftSkillsComplementarity from "./short-reports/user-team-soft-skills-complementarity";
import UserTeamLeadershipContrast from "./short-reports/user-team-leadership-contrast";
import UserTeamCulturalFit from "./short-reports/user-team-cultural-fit";

import UserRoleMobilityFeasibilityReport from "./short-reports/user-role-mobility-feasibility-report";
import UserRolePersonalityFitReport from "./short-reports/user-role-personality-fit-report";
import UserRoleSkillMatchReport from "./short-reports/user-role-skill-match-report";

// survives re-renders and route remounts, resets only on real page reload
let __didInitThisPageLoad = false;

const AnalysisComparisonPage = () => {
  const { t } = useTranslation();
  const restoredRef = useRef(false); // block stashing until we've tried restoring

  // Wipe snapshot on real unload/refresh so the next load starts clean
  useEffect(() => {
    // Remove any saved snapshot from the current history entry (works across reloads)
    const clearSnapshotInHistoryNative = () => {
      try {
        const st = window.history.state || {};
        const usr = { ...(st.usr || {}) }; // React Router v6 stores user state in history.state.usr
        if ("_analysisSnapshot" in usr) usr._analysisSnapshot = undefined;
        window.history.replaceState({ ...st, usr }, ""); // no navigate() during unload
      } catch {}
    };

    const onBeforeUnload = () => clearSnapshotInHistoryNative();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const stashTimerRef = useRef(null);
  const [activeComparisonTab, setActiveComparisonTab] = useState("entities");
  const [selectedPeople, setSelectedPeople] = useState([]);

  // const [activeComparisonTab, setActiveComparisonTab] = useState(() => {
  // return sessionStorage.getItem("analysisComparisonTab") || "entities";
  // });
  const [entities, setEntities] = useState([
    { id: 1, type: "", name: "", data: null },
    { id: 2, type: "", name: "", data: null },
  ]);

  const [selectedEntityType, setSelectedEntityType] = useState("");
  const [selectedEntityName, setSelectedEntityName] = useState("");

  // const [selectedPeople, setSelectedPeople] = useState(() => {
  // const saved = sessionStorage.getItem("analysisSelectedPeople");
  // return saved ? JSON.parse(saved) : [];
  // });
  const [comparisonResult, setComparisonResult] = useState(null);
  const [isComparing, setIsComparing] = useState(false);
  const [showAnalysisQuestions, setShowAnalysisQuestions] = useState(false);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({
    role: "",
    performanceRange: "",
    experience: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const entityTypes = [
    { value: "team", label: "Team" },
    { value: "department", label: "Department" },
    { value: "people", label: "People" },
    { value: "role", label: "Role" },
  ];

  const allowedEntityPairs = [
    ["team", "team"],
    ["department", "department"],
    ["people", "people"],
    ["people", "team"],
    ["team", "people"],
    ["people", "role"],
    ["role", "people"],
  ];

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message ||
          "Please upgrade your plan to continue using this feature."
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  // const pairSet = new Set(allowedEntityPairs.map(([a, b]) => `${a}|${b}`));
  // const isAllowedPair = (a, b) =>
  //   !!(pairSet.has(`${a}|${b}`) || pairSet.has(`${b}|${a}`));

  const pairSet = new Set(allowedEntityPairs.map(([a, b]) => `${a}|${b}`));
  const isAllowedPair = (a, b) => pairSet.has(`${a}|${b}`);

  const allTypeValues = entityTypes.map((t) => t.value);

  const getAllowedTypesGivenOther = (otherType) => {
    if (!otherType) return allTypeValues; // if other side not chosen yet, show all
    return allTypeValues.filter((t) => isAllowedPair(t, otherType));
  };

  /* ["role", "role"],
    ["team", "department"],
    ["role", "team"],
    ["user", "role"],
    ["user", "team"], */

  const peopleEntityTypes = [
    { value: "team", label: "Team" },
    { value: "department", label: "Department" },
    { value: "role", label: "Role" },
  ];

  useEffect(() => {
    // Fetch initial data or perform setup actions
    getAllTeamsApiHandler();
    getPeopleApiHandler();
    getJobRolesApiHandler();
  }, []);

  const [teamsDataList, setTeamsDataList] = useState([]);
  const getAllTeamsApiHandler = async () => {
    try {
      const response = await getAllTeamsApi();
      setTeamsDataList(response.data || []);
    } catch (error) {
      console.error("Error fetching teams data:", error);
      handleApiError(error, {
        showModal: false,
        showButton: false,
      });
    }
  };
  const [jobRolesDataList, setJobRolesDataList] = useState([]);
  const getJobRolesApiHandler = async () => {
    try {
      const response = await getJobRolesDropDownApi();
      setJobRolesDataList(response.data || []);
    } catch (error) {
      console.error("Error fetching job roles data:", error);
      handleApiError(error, {
        showModal: false,
        showButton: false,
      });
    }
  };
  const [peopleDataList, setPeopleDataList] = useState([]);
  const getPeopleApiHandler = async () => {
    try {
      const response = await getAllPeopleApi();
      setPeopleDataList(response.data || []);
    } catch (error) {
      console.error("Error fetching people data:", error);
      handleApiError(error, {
        showModal: false,
        showButton: false,
      });
    }
  };
  useEffect(() => {
    if (!teamsDataList.length) return;

    setEntities((prev) =>
      prev.map((e) => {
        if (e.type !== "team" || !e.name) return e;
        const match = teamsDataList.find(
          (t) => String(t._id) === String(e.name)
        );
        if (!match) return e;
        return { ...e, data: { id: String(match._id), name: match.teamName } };
      })
    );
  }, [teamsDataList]);

  // Department list state
  const [departments, setDepartments] = useState([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);

  const loadDepartments = useCallback(async () => {
    setIsLoadingDepartments(true);
    try {
      // API returns { code, message, data: [...] }
      const res = await getDepartmentsListApi(); // uses /api/v3/home/getDepartments
      const list = Array.isArray(res?.data) ? res.data : [];
      setDepartments(list);
    } catch (err) {
      // Use your existing error handler if present
      if (typeof handleApiError === "function") {
        handleApiError(err, { showModal: true });
      } else {
        console.error("Failed to load departments", err);
      }
    } finally {
      setIsLoadingDepartments(false);
    }
  }, []);

  // Sample data
  const sampleData = {
    team: teamsDataList.map((team) => ({
      // id: team._id,

      id: String(team._id),
      name: team.teamName,
    })),
    department: [
      { id: 1, name: "Engineering", employees: 25 },
      { id: 2, name: "Marketing", employees: 15 },
      { id: 3, name: "Sales", employees: 20 },
      { id: 4, name: "HR", employees: 8 },
    ],
    role: jobRolesDataList.map((role) => ({
      id: String(role.jobId),
      name: role.roleName,
    })),
    // role: [
    //   { id: 1, name: "Software Engineer", level: "Mid-Senior" },
    //   { id: 2, name: "Product Manager", level: "Senior" },
    //   { id: 3, name: "UX Designer", level: "Mid" },
    //   { id: 4, name: "Data Analyst", level: "Junior-Mid" },
    // ],
    // People: [
    //   { id: 1, name: "John Smith", role: "Software Engineer" },
    //   { id: 2, name: "Sarah Johnson", role: "Product Manager" },
    //   { id: 3, name: "Mike Chen", role: "UX Designer" },
    //   { id: 4, name: "Emily Davis", role: "Data Analyst" },
    // ],

    people: peopleDataList.map((people) => ({
      // id: String(people.userId),
      id: String(people._id),
      name: people.name,
    })),
  };

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  const samplePeopleData = {
    team: {
      "Engineering Team": [
        {
          id: 1,
          name: "John Smith",
          role: "Software Engineer",
          performance: 95,
          experience: "5 years",
          skills: ["React", "Node.js"],
          team: "Engineering Team",
          department: "Engineering",
        },
        {
          id: 2,
          name: "Alice Brown",
          role: "UX Designer",
          performance: 88,
          experience: "3 years",
          skills: ["Vue.js", "CSS"],
          team: "Engineering Team",
          department: "Engineering",
        },
        {
          id: 3,
          name: "Bob Wilson",
          role: "Product Manager",
          performance: 92,
          experience: "4 years",
          skills: ["Strategy", "Analytics"],
          team: "Engineering Team",
          department: "Engineering",
        },
      ],
      "Design Team": [
        {
          id: 4,
          name: "Sarah Johnson",
          role: "UX Designer",
          performance: 92,
          experience: "4 years",
          skills: ["Figma", "User Research"],
          team: "Design Team",
          department: "Engineering",
        },
        {
          id: 5,
          name: "Tom Davis",
          role: "UI Designer",
          performance: 85,
          experience: "2 years",
          skills: ["Sketch", "Prototyping"],
          team: "Design Team",
          department: "Engineering",
        },
      ],
    },
    department: {
      Engineering: [
        {
          id: 1,
          name: "John Smith",
          role: "Software Engineer",
          performance: 95,
          experience: "5 years",
          skills: ["React", "Node.js"],
          team: "Engineering Team",
          department: "Engineering",
        },
        {
          id: 2,
          name: "Sarah Johnson",
          role: "UX Designer",
          performance: 92,
          experience: "4 years",
          skills: ["Figma", "User Research"],
          team: "Design Team",
          department: "Engineering",
        },
        {
          id: 3,
          name: "Mike Chen",
          role: "Product Manager",
          performance: 88,
          experience: "6 years",
          skills: ["Strategy", "Analytics"],
          team: "Product Team",
          department: "Engineering",
        },
      ],
      Marketing: [
        {
          id: 6,
          name: "Emily Davis",
          role: "Marketing Specialist",
          performance: 85,
          experience: "3 years",
          skills: ["SEO", "Content"],
          team: "Marketing Team",
          department: "Marketing",
        },
        {
          id: 7,
          name: "Chris Lee",
          role: "Social Media Manager",
          performance: 82,
          experience: "2 years",
          skills: ["Social Media", "Analytics"],
          team: "Marketing Team",
          department: "Marketing",
        },
      ],
    },
    role: {
      "Software Engineer": [
        {
          id: 1,
          name: "John Smith",
          role: "Software Engineer",
          performance: 95,
          experience: "5 years",
          skills: ["React", "Node.js"],
          team: "Engineering Team",
          department: "Engineering",
        },
        {
          id: 8,
          name: "David Kim",
          role: "Software Engineer",
          performance: 93,
          experience: "7 years",
          skills: ["Java", "Spring"],
          team: "Backend Team",
          department: "Engineering",
        },
      ],
      "UX Designer": [
        {
          id: 4,
          name: "Sarah Johnson",
          role: "UX Designer",
          performance: 92,
          experience: "4 years",
          skills: ["Figma", "User Research"],
          team: "Design Team",
          department: "Engineering",
        },
        {
          id: 9,
          name: "Lisa Wang",
          role: "UX Designer",
          performance: 89,
          experience: "3 years",
          skills: ["Adobe XD", "Wireframing"],
          team: "Design Team",
          department: "Engineering",
        },
      ],
    },
  };
  const [selectedEntityValue1, setSelectedEntityValue1] = useState(null);
  const [selectedEntityValue2, setSelectedEntityValue2] = useState(null);
  const [selectedEntityType1, setSelectedEntityType1] = useState("");
  const [selectedEntityType2, setSelectedEntityType2] = useState("");

  useEffect(() => {
    if (activeComparisonTab !== "entities") return;

    const bothSelected = entities.every((e) => e.type && e.name && e.data);
    if (!bothSelected) {
      setShowAnalysisQuestions(false);
      return;
    }

    const types = entities.map((e) => e.type);
    const isAllowedPair = allowedEntityPairs.some(
      (pair) =>
        (pair[0] === types[0] && pair[1] === types[1]) ||
        (pair[0] === types[1] && pair[1] === types[0])
    );

    setShowAnalysisQuestions(isAllowedPair);
  }, [entities, activeComparisonTab]);

  // Build a simple signature for the current pair
  const pairSig = (arr) =>
    (arr || []).map((e) => `${e.type || ""}:${e.name || ""}`).join("|");

  const updateEntity = (index, field, value) => {
    const next = entities.map((entity, i) => {
      if (i !== index) return entity;

      if (field === "type") {
        // reset selection when type changes
        return { ...entity, type: value, name: "", data: null };
      }

      if (field === "name") {
        const typeForThis = entities[index].type;
        const strVal = String(value); // normalize to string id

        // Choose the correct source list based on type
        let sourceList = [];
        if (typeForThis === "department") {
          // from API
          sourceList = (departments || []).map((d) => ({
            id: String(d._id ?? d.id ?? d.name ?? ""),
            name: d.name ?? "",
          }));
        } else {
          // from local samples (team/role/etc.)
          sourceList = (sampleData[typeForThis] || []).map((item) => ({
            id: String(item.id ?? item.value ?? item.name ?? ""),
            name: item.name ?? item.label ?? "",
          }));
        }

        const entityData =
          sourceList.find((item) => String(item.id) === strVal) || null;

        return { ...entity, name: strVal, data: entityData };
      }

      return entity;
    });
    // Only enforce dependency when Entity 1 changes
    if (field === "type" && index === 0) {
      const firstType = value;
      const secondType = next[1]?.type;
      if (secondType && !isAllowedPair(secondType, firstType)) {
        next[1] = { ...next[1], type: "", name: "", data: null };
      }
      // clear analysis since the pair changed
      setSelectedAnalysisType("");
      setComparisonResult(null);
      generatedOnceRef.current.entities = false;
    }

    setEntities(next);
    // detect pair change and reset analysis state
    const prevSignature = pairSig(entities);
    const nextSignature = pairSig(next);
    const didPairChange = prevSignature !== nextSignature;
    if (didPairChange) {
      setSelectedAnalysisType("");
      setComparisonResult(null);
      generatedOnceRef.current.entities = false;
    }

    setEntities(next);

    if (index === 0 && field === "name") setSelectedEntityValue1(String(value));
    if (index === 1 && field === "name") setSelectedEntityValue2(String(value));
    if (index === 0 && field === "type") setSelectedEntityType1(value);
    if (index === 1 && field === "type") setSelectedEntityType2(value);

    generatedOnceRef.current.entities = false;

    // gate showing analysis questions
    const bothSelected = next.every((e) => e.type && e.name && e.data);
    if (bothSelected && activeComparisonTab === "entities") {
      const types = next.map((e) => e.type);
      const isAllowedPair = allowedEntityPairs.some(
        (pair) =>
          (pair[0] === types[0] && pair[1] === types[1]) ||
          (pair[0] === types[1] && pair[1] === types[0])
      );
      if (!isAllowedPair) {
        setShowAnalysisQuestions(false);
        alert("This entity pair combination is not allowed...");
      } else {
        setShowAnalysisQuestions(true);
      }
    } else {
      setShowAnalysisQuestions(false);
    }
  };

  // Builds a stable key for the selected entity-pair (keeps order)
  const getEntityComboKey = () => {
    if (!comparisonResult?.entities || comparisonResult.entities.length < 2)
      return "unknown-unknown";
    const [e1, e2] = comparisonResult.entities;
    return `${e1?.type || "unknown"}-${e2?.type || "unknown"}`;
  };
  // Build a snapshot of everything we want to restore when coming back
  const buildSnapshot = () => ({
    activeComparisonTab,
    entities,
    selectedEntityType,
    selectedEntityName,
    selectedPeople,
    selectedAnalysisType,
    comparisonResult,
    selectedEntityValue1,
    selectedEntityValue2,
    selectedEntityType1,
    selectedEntityType2,
  });

  // keep your existing buildSnapshot()
  // useEffect(() => {
  //   // debounce to avoid spamming navigate() on rapid changes
  //   if (stashTimerRef.current) clearTimeout(stashTimerRef.current);
  //   stashTimerRef.current = window.setTimeout(() => {
  //     const snapshot = buildSnapshot();
  //     navigate(".", {
  //       replace: true,
  //       state: { ...(location.state || {}), _analysisSnapshot: snapshot },
  //     });
  //   }, 0);
  //   return () => {
  //     if (stashTimerRef.current) clearTimeout(stashTimerRef.current);
  //   };
  // }, [
  //   activeComparisonTab,
  //   entities,
  //   selectedPeople,
  //   selectedEntityType,
  //   selectedEntityName,
  //   selectedAnalysisType,
  //   selectedEntityType1,
  //   selectedEntityType2,
  //   selectedEntityValue1,
  //   selectedEntityValue2,
  //   comparisonResult,
  // ]);

  useEffect(() => {
    if (!restoredRef.current) return; // <-- do not stash before we restore/decide
    if (stashTimerRef.current) clearTimeout(stashTimerRef.current);
    // small delay prevents thrashing while multiple state setters run
    stashTimerRef.current = window.setTimeout(() => {
      const snapshot = buildSnapshot();
      navigate(".", {
        replace: true,
        state: { ...(location.state || {}), _analysisSnapshot: snapshot },
      });
    }, 50);
    return () => {
      if (stashTimerRef.current) clearTimeout(stashTimerRef.current);
    };
  }, [
    activeComparisonTab,
    entities,
    selectedPeople,
    selectedEntityType,
    selectedEntityName,
    selectedAnalysisType,
    selectedEntityType1,
    selectedEntityType2,
    selectedEntityValue1,
    selectedEntityValue2,
    comparisonResult,
  ]);

  // Keep the latest snapshot in a ref so cleanup saves the newest values
  const latestSnapshotRef = useRef(buildSnapshot());
  useEffect(() => {
    latestSnapshotRef.current = buildSnapshot();
  });

  // const stashSnapshotInHistory = (override = {}) => {
  //   const snapshot = { ...buildSnapshot(), ...override };
  //   navigate(".", {
  //     replace: true,
  //     state: { ...(location.state || {}), _analysisSnapshot: snapshot },
  //   });
  // };

  const generateComparison = async () => {
    console.log("Generating comparison...");

    if (!selectedAnalysisType && activeComparisonTab === "entities") {
      alert("Please select an analysis type before generating the comparison.");
      return null;
    }

    const t1 = selectedEntityType1;
    const t2 = selectedEntityType2;

    // Detect User↔Role (both orders)
    // const isUserRole =      (t1 === "people" && t2 === "role") || (t1 === "role" && t2 === "people");

    // For any pairs other than Team↔Team or Department↔Department
    // (and regardless, for User↔Role specifically), do NOT hit the API.
    const shouldHitApiBase =
      (t1 === "team" && t2 === "team") ||
      (t1 === "department" && t2 === "department") ||
      (t1 === "people" && t2 === "people") ||
      (t1 === "people" && t2 === "team") ||
      (t1 === "team" && t2 === "people") ||
      (t1 === "people" && t2 === "role") ||
      (t1 === "role" && t2 === "people");

    // Override to ensure User↔Role always stays offline
    const shouldHitApi = shouldHitApiBase;

    if (!shouldHitApi) {
      const dummy = {
        type: "entities",
        analysisType: selectedAnalysisType,
        entities: [
          { type: t1, data: entities[0]?.data || null },
          { type: t2, data: entities[1]?.data || null },
        ],
      };
      setComparisonResult(dummy);
      return dummy;
    }

    // --- API path (unchanged) ---
    setIsComparing(true);
    try {
      const entity1Val =
        t1 === "department"
          ? entities[0]?.data?.name ?? selectedEntityValue1
          : selectedEntityValue1;

      const entity2Val =
        t2 === "department"
          ? entities[1]?.data?.name ?? selectedEntityValue2
          : selectedEntityValue2;

      const req = {
        entity1: { value: entity1Val, type: t1 },
        entity2: { value: entity2Val, type: t2 },
        comparisonType: selectedAnalysisType,
      };

      console.log("Comparison Request:", req);
      const response = await getGenerateComparisonAnalysis(req);

      setComparisonResult(response.data);
      setIsComparing(false);
      return response.data;
    } catch (error) {
      handleApiError(error, { showModal: true });
      setComparisonResult(null);
      setIsComparing(false);
      return null;
    }
  };

  useEffect(() => {
    if (activeComparisonTab !== "entities") return;

    // must have a chosen analysis type AND two selected entities
    if (!selectedAnalysisType) return;
    const bothSelected = entities.every((e) => e.type && e.name);
    if (!bothSelected) return;

    const key = getCurrentCompareKey();

    // If cached, reuse and stop.
    if (comparisonCacheRef.current.has(key)) {
      setComparisonResult(comparisonCacheRef.current.get(key));
      generatedOnceRef.current.entities = true;
      return;
    }

    // If we've already run once this activation (and nothing cached), bail.
    if (generatedOnceRef.current.entities) return;

    // Mark as ran before calling (prevents double-runs on quick state churn)
    generatedOnceRef.current.entities = true;

    setComparisonResult(null);

    (async () => {
      const data = await generateComparison();
      if (data) {
        comparisonCacheRef.current.set(key, data);
      } else {
        // If the call failed, allow another attempt by clearing the guard
        generatedOnceRef.current.entities = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeComparisonTab,
    selectedAnalysisType,
    entities,
    selectedEntityType1,
    selectedEntityType2,
    selectedEntityValue1,
    selectedEntityValue2,
  ]);

  // run-once guards per top-level tab
  const generatedOnceRef = useRef({
    entities: false,
    people: false,
  });

  // Build options for the entity dropdowns
  const departmentOptions = useMemo(
    () =>
      departments.map((d) => ({
        value: d._id, // prefer _id for stable identity
        label: d.name ?? "", // display name
      })),
    [departments]
  );

  // Example helper: returns options by type
  const getOptionsForType = useCallback(
    (type) => {
      switch (type) {
        case "department":
          return departmentOptions;
        // case "team": return teamOptions; // whatever you already have
        // case "role": return roleOptions;
        default:
          return [];
      }
    },
    [departmentOptions]
  );

  // cache results: key -> payload
  const comparisonCacheRef = useRef(new Map());

  // stable key for “this selection”
  const getCurrentCompareKey = () =>
    [
      `tab:${activeComparisonTab}`, // "entities"
      `e1:${selectedEntityType1}:${selectedEntityValue1}`,
      `e2:${selectedEntityType2}:${selectedEntityValue2}`,
    ].join("|");

  // WITH this:
  const analysisLabelFromKey = (k) => {
    switch (k) {
      case "personality":
        return "Personality Distribution";
      case "leadership":
        return "Leadership Metrics";
      case "internal-mobility-metrics":
        return "Internal Mobility Metrics";
      case "aggregate-skill-gaps":
        return "Aggregate Skill Gaps";
      case "upskilling-readiness":
        return "Upskilling Readiness";
      case "skill-gap":
        return "Soft Skills Gaps";
      case "soft-skills-complementarity":
        return "Soft-Skills Complementarity";
      case "leadership-contrast":
        return "Leadership Contrast";
      case "cultural-fit":
        return "Cultural Fit";
      case "mobility-feasibility":
        return "Mobility Feasibility";
      case "personality-fit":
        return "Personality Fit";
      case "skill-match":
        return "Skill Match";

      default:
        return "Analysis";
    }
  };

  const renderReport = () => {
    if (!comparisonResult) return null;

    // Identify the specific short report: 8 combos × 3 analysis types = 24
    // Example keys: "team-team::skill-gap", "role-team::leadership-metrics", etc.
    // const comboKey = getEntityComboKey();
    // const typeKey =
    //   selectedAnalysisType ||
    //   comparisonResult.analysisType ||
    //   "Soft Skills Gaps";
    // const reportKey = `${comboKey}::${typeKey}`;
    const comboKey = getEntityComboKey();
    const analysisKey = normalizeAnalysisKey(
      selectedAnalysisType || comparisonResult.analysisType
    );
    const patchedData = {
      ...comparisonResult,
      analysisType: analysisLabelFromKey(analysisKey),
    };
    const reportKey = `${comboKey}::${analysisKey}`;

    // Choose the short-report component by analysis type.
    // We pass both the full comparison payload and a stable reportKey so the child can specialize per combo.
    switch (analysisKey) {
      case "skill-gap":
        return (
          <SkillGapAnalysisReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "personality":
        return (
          <PersonalityDistributionReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "leadership":
        return (
          <LeadershipMetricsReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "internal-mobility-metrics":
        return (
          <InternalMobilityReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "aggregate-skill-gaps":
        return (
          <AggregateSkillGapsReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "upskilling-readiness":
        return (
          <UpskillingReadinessReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "skill-gap-delta":
        return (
          <UserSkillGapDeltaReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "mobility-readiness":
        return (
          <UserMobilityReadinessReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "soft-skills-complementarity":
        return (
          <UserTeamSoftSkillsComplementarity
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "leadership-contrast":
        return (
          <UserTeamLeadershipContrast
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "cultural-fit":
        return (
          <UserTeamCulturalFit
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "mobility-feasibility":
        return (
          <UserRoleMobilityFeasibilityReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "personality-fit":
        return (
          <UserRolePersonalityFitReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "skill-match":
        return (
          <UserRoleSkillMatchReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );

      case "leadership-potential":
      default:
        return (
          <UserLeadershipPotentialReport
            comparisonData={comparisonResult}
            reportKey={reportKey}
          />
        );
    }
  };

  // Reset pagination when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    resetPagination();
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedFilters({
      role: "",
      performanceRange: "",
      experience: "",
    });
    resetPagination();
  };

  // const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  // const handleDetailedComparison = () => {
  //   if (!comparisonResult) return;

  //   // Map short-analysis type → detailed report id used by DetailedComparisonPage
  //   const idMap = {
  //     "skill-gap": "tt1",
  //     "Personality Distribution": "tt2",
  //     "Leadership Metrics": "tt3",
  //   };

  //   const reportId = idMap[selectedAnalysisType] || "tt1";

  //   // Navigate with a query param for the detailed component selector
  //   // and pass the whole comparison result as state so the detailed page
  //   // can render the correct data for ANY of the 8 entity combinations.
  //   navigate(`/reports/detailed-comparison?id=${reportId}`, {
  //     state: { comparisonData: comparisonResult },
  //   });
  // };

  const handleDetailedComparison = () => {
    if (!comparisonResult) return;

    const snapshot = buildSnapshot();
    navigate(".", {
      replace: true, // update current entry
      state: { ...(location.state || {}), _analysisSnapshot: snapshot },
    });

    const key = normalizeAnalysisKey(
      selectedAnalysisType || comparisonResult?.analysisType
    );

    // Dept↔Dept should use dd1/dd2/dd3; Team↔Team should use tt1/tt2/tt3
    const isDeptVsDept =
      selectedEntityType1 === "department" &&
      selectedEntityType2 === "department";

    const mapTT = {
      "skill-gap": "tt1",
      personality: "tt2",
      leadership: "tt3",
    };

    const mapDD = {
      "internal-mobility-metrics": "dd1",
      "aggregate-skill-gaps": "dd2",
      "upskilling-readiness": "dd3",
      // label-style and direct ids
      "internal mobility metrics": "dd1",
      "aggregate skill gaps": "dd2",
      "upskilling readiness": "dd3",
      dd1: "dd1",
      dd2: "dd2",
      dd3: "dd3",
    };

    const mapUU = {
      // detailed User↔User reports
      "skill-gap-delta": "uu1",
      "mobility-readiness": "uu2",
      "leadership-potential": "uu3",
      // label-style and direct ids
      "skill gap delta": "uu1",
      "mobility readiness": "uu2",
      "leadership potential": "uu3",
      uu1: "uu1",
      uu2: "uu2",
      uu3: "uu3",
    };

    // User↔Team
    const mapUT = {
      "soft-skills-complementarity": "ut1",
      "leadership-contrast": "ut2",
      "cultural-fit": "ut3",
      // label-style and direct ids
      "soft skills complementarity": "ut1",
      "leadership contrast": "ut2",
      "cultural fit": "ut3",
      ut1: "ut1",
      ut2: "ut2",
      ut3: "ut3",
    };

    // NEW: User↔Role
    const mapUR = {
      "mobility-feasibility": "ur1",
      "personality-fit": "ur2",
      "skill-match": "ur3",
      // label-style and direct ids
      "mobility feasibility": "ur1",
      "personality fit": "ur2",
      "skill match": "ur3",
      ur1: "ur1",
      ur2: "ur2",
      ur3: "ur3",
    };

    const normalizedKey = (key || "").toString().trim().toLowerCase();

    const isUserVsUser =
      selectedEntityType1 === "people" && selectedEntityType2 === "people";

    const isUserTeam =
      (selectedEntityType1 === "people" && selectedEntityType2 === "team") ||
      (selectedEntityType1 === "team" && selectedEntityType2 === "people");

    // NEW: detect User↔Role (both orders)
    const isUserRole =
      (selectedEntityType1 === "people" && selectedEntityType2 === "role") ||
      (selectedEntityType1 === "role" && selectedEntityType2 === "people");

    // Choose reportId by pair type
    const reportId = isDeptVsDept
      ? mapDD[normalizedKey] || "dd1"
      : isUserVsUser
      ? mapUU[normalizedKey] || "uu1"
      : isUserTeam
      ? mapUT[normalizedKey] || "ut1"
      : isUserRole
      ? mapUR[normalizedKey] || "ur1"
      : mapTT[normalizedKey] || "tt1";

    navigate(`/reports/detailed-comparison?id=${reportId}`, {
      state: { comparisonData: comparisonResult },
    });
  };

  const normalizeAnalysisKey = (raw) => {
    const s = (raw ?? "").toString().trim().toLowerCase();

    // --- User↔User short-report keys (keep first) ---
    if (s.includes("leadership") && s.includes("potential"))
      return "leadership-potential";
    if (s.includes("skill") && s.includes("delta")) return "skill-gap-delta";
    if (s.includes("mobility") && s.includes("readiness"))
      return "mobility-readiness";
    if (s === "uu1") return "skill-gap-delta";
    if (s === "uu2") return "mobility-readiness";
    if (s === "uu3") return "leadership-potential";

    // --- User↔Team mappings (put BEFORE generic "personality"/"skill") ---
    if (s.includes("soft") && s.includes("complement"))
      return "soft-skills-complementarity";
    if (s.includes("leadership") && s.includes("contrast"))
      return "leadership-contrast";
    // Support both "team-cultural-fit" and "cultural-fit"
    if (s.includes("cultural") && s.includes("fit")) return "cultural-fit";
    if (s === "ut1") return "soft-skills-complementarity";
    if (s === "ut2") return "leadership-contrast";
    if (s === "ut3") return "cultural-fit";

    // --- User↔Role mappings (put BEFORE generic "personality"/"skill") ---
    // Cards emit: "skill-match-index", "personality-fit-for-role", "mobility-feasibility"
    if (s.includes("mobility") && s.includes("feas"))
      return "mobility-feasibility";
    if (s.includes("personality") && s.includes("fit"))
      return "personality-fit"; // handles "...for-role"
    if (s.includes("skill") && s.includes("match")) return "skill-match"; // handles "...-index"
    if (s === "ur1") return "mobility-feasibility";
    if (s === "ur2") return "personality-fit";
    if (s === "ur3") return "skill-match";

    // --- Dept↔Dept IDs ---
    if (s.includes("internal") && s.includes("mobility"))
      return "internal-mobility-metrics";
    if (s.includes("aggregate") && s.includes("skill"))
      return "aggregate-skill-gaps";
    if (s.includes("upskill")) return "upskilling-readiness";
    if (s === "dd1") return "internal-mobility-metrics";
    if (s === "dd2") return "aggregate-skill-gaps";
    if (s === "dd3") return "upskilling-readiness";

    // --- Team↔Team / legacy IDs & labels (generic catch-alls) ---
    if (s.includes("personality")) return "personality";
    if (s.includes("leader")) return "leadership";
    if (s.includes("skill") || s.includes("soft")) return "skill-gap";
    if (s === "tt2") return "personality";
    if (s === "tt3") return "leadership";

    return s;
  };

  useEffect(() => {
    // If we just reloaded, beforeunload already cleared the snapshot,
    // so there will be nothing to restore. If we came back via Back,
    // the snapshot will be present and we restore it.
    const snap = location.state?._analysisSnapshot;
    if (snap) {
      setActiveComparisonTab(snap.activeComparisonTab ?? "entities");
      setEntities(
        snap.entities ?? [
          { id: 1, type: "", name: "", data: null },
          { id: 2, type: "", name: "", data: null },
        ]
      );
      setSelectedEntityType(snap.selectedEntityType ?? "");
      setSelectedEntityName(snap.selectedEntityName ?? "");
      setSelectedPeople(snap.selectedPeople ?? []);
      setSelectedAnalysisType(snap.selectedAnalysisType ?? "");
      setComparisonResult(snap.comparisonResult ?? null);
      setSelectedEntityValue1(snap.selectedEntityValue1 ?? null);
      setSelectedEntityValue2(snap.selectedEntityValue2 ?? null);
      setSelectedEntityType1(snap.selectedEntityType1 ?? "");
      setSelectedEntityType2(snap.selectedEntityType2 ?? "");

      if (snap.comparisonResult) {
        generatedOnceRef.current.entities = true;
        // const key = [
        //   `tab:${snap.activeComparisonTab}`,
        //   `e1:${snap.selectedEntityType1}:${snap.selectedEntityValue1}`,
        //   `e2:${snap.selectedEntityType2}:${snap.selectedEntityValue2}`,
        //   `atype:${(snap.selectedAnalysisType || "").toString().toLowerCase()}`,
        // ].join("|");
        const key = [
          `tab:${snap.activeComparisonTab}`,
          `e1:${snap.selectedEntityType1}:${snap.selectedEntityValue1}`,
          `e2:${snap.selectedEntityType2}:${snap.selectedEntityValue2}`,
        ].join("|");
        comparisonCacheRef.current.set(key, snap.comparisonResult);
      }
    }
    restoredRef.current = true; // allow stashing after we tried to restore
  }, []); // once

  // Get available entity names based on selected entity type
  const getAvailableEntityNames = () => {
    if (!selectedEntityType) return [];
    return Object.keys(samplePeopleData[selectedEntityType] || {});
  };

  const getAvailablePeople = () => {
    if (!selectedEntityType || !selectedEntityName) return [];
    return samplePeopleData[selectedEntityType]?.[selectedEntityName] || [];
  };

  const handleEntityTypeChange = (entityType) => {
    setSelectedEntityType(entityType);
    setSelectedEntityName("");
    setSelectedPeople([]);
    clearAllFilters();
  };

  const handleEntityNameChange = (entityName) => {
    setSelectedEntityName(entityName);
    setSelectedPeople([]);
    clearAllFilters();
  };

  const handlePersonSelection = (person) => {
    const updatedSelectedPeople = selectedPeople.some((p) => p.id === person.id)
      ? selectedPeople.filter((p) => p.id !== person.id)
      : [...selectedPeople, person];
    setSelectedPeople(updatedSelectedPeople);

    // Show analysis questions when 2 or more people are selected
    if (updatedSelectedPeople.length >= 2 && activeComparisonTab === "people") {
      setShowAnalysisQuestions(true);
    } else {
      setShowAnalysisQuestions(false);
      setSelectedAnalysisType("");
    }
  };

  const getFilteredPeople = () => {
    if (!selectedEntityType || !selectedEntityName) return [];

    let people =
      samplePeopleData[selectedEntityType]?.[selectedEntityName] || [];

    // Apply search filter
    if (searchTerm) {
      people = people.filter(
        (person) =>
          person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          person.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
          person.skills.some((skill) =>
            skill.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    // Apply role filter
    if (selectedFilters.role) {
      people = people.filter((person) => person.role === selectedFilters.role);
    }

    // Apply performance range filter
    if (selectedFilters.performanceRange) {
      const [min, max] = selectedFilters.performanceRange
        .split("-")
        .map(Number);
      people = people.filter(
        (person) => person.performance >= min && person.performance <= max
      );
    }

    // Apply experience filter
    if (selectedFilters.experience) {
      people = people.filter((person) => {
        const experience = Number.parseInt(person.experience);
        switch (selectedFilters.experience) {
          case "0-2":
            return experience <= 2;
          case "3-5":
            return experience >= 3 && experience <= 5;
          case "6+":
            return experience >= 6;
          default:
            return true;
        }
      });
    }

    // Sort by performance (highest first)
    return people.sort((a, b) => {
      return b.performance - a.performance;
    });
  };

  // Check if person is recommended for selected entity
  const isPersonRecommended = (person) => {
    if (!selectedEntityType || !selectedEntityName) return false;

    switch (selectedEntityType) {
      case "team":
        return person.team === selectedEntityName;
      case "department":
        return person.department === selectedEntityName;
      case "role":
        return person.role === selectedEntityName;
      default:
        return false;
    }
  };

  // Get paginated people
  const getPaginatedPeople = () => {
    const filteredPeople = getFilteredPeople();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPeople.slice(startIndex, endIndex);
  };

  // Get total pages
  const getTotalPages = () => {
    const filteredPeople = getFilteredPeople();
    return Math.ceil(filteredPeople.length / itemsPerPage);
  };

  const getDynamicAnalysisQuestions = () => {
    if (activeComparisonTab === "people") {
      return [];
    }

    if (activeComparisonTab === "entities") {
      const entity1Type = entities[0]?.type;
      const entity2Type = entities[1]?.type;

      return getAnalysisQuestions(
        entity1Type,
        entity2Type,
        activeComparisonTab,
        entities
      );
    }

    return [];
  };

  return (
    <div className="analysis-comparison-page">
      <BackButton />

      <main className="analysis-comparison-main">
        <div className="container">
          {/* Header */}
          <div className="analysis-comparison-header">
            <div className="analysis-header-content">
              <div className="analysis-header-icon">
                <BarChart3 size={40} />
              </div>
              <div className="analysis-header-text">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    justifyContent: "center",
                  }}
                >
                  <h1>Analysis & Comparison Reports</h1>
                  <CustomTooltip
                    tooltip={{
                      title: t("AnalysisComparisonReports.tooltips.title"),
                      message: t("AnalysisComparisonReports.tooltips.message"),
                    }}
                    id="mobility-recommendation-info"
                    className="compact-tooltip"
                  >
                    <Info
                      size={18}
                      style={{
                        marginTop: "10px",
                        color: "var(--text-secondary)",
                        cursor: "pointer",
                      }}
                    />
                  </CustomTooltip>
                </div>
                <p>
                  Compare entities and generate detailed performance insights
                </p>
              </div>
            </div>
          </div>

          {/* Comparison Type Tabs */}
          <div className="comparison-type-tabs-unique">
            <button
              className={`comparison-tab-btn-unique ${
                activeComparisonTab === "entities" ? "active" : ""
              }`}
              onClick={() => {
                setActiveComparisonTab("entities");
                setShowAnalysisQuestions(false);
                setSelectedAnalysisType("");
              }}
            >
              <BarChart3 size={16} />
              Compare Entities
            </button>
            {/* <button
              className={`comparison-tab-btn-unique ${
                activeComparisonTab === "people" ? "active" : ""
              } disabled`}
              disabled={true}
              onClick={() => {
                // setActiveComparisonTab("people");
                // setShowAnalysisQuestions(false);
                // setSelectedAnalysisType("");
              }}
            >
              <Users size={16} />
              Compare People by Entity
            </button> */}
          </div>

          {/* Entity Selection Section */}
          {activeComparisonTab === "entities" && (
            <div className="entity-selection-section">
              <div className="section-header">
                <h2>Select 2 Entities to Compare</h2>
              </div>

              <div className="entities-grid-fixed-unique">
                {entities.map((entity, index) => {
                  // Build options per type
                  let options = [];
                  if (entity.type === "department") {
                    // Use API-backed departments
                    options = (departments || []).map((d) => ({
                      value: String(d._id ?? d.id ?? d.name ?? ""),
                      label: d.name ?? "",
                    }));
                  } else {
                    // Use existing local sampleData for other types
                    options = (sampleData[entity.type] || []).map((item) => ({
                      value: String(item.id ?? item.value ?? item.name ?? ""),
                      label: item.name ?? item.label ?? "",
                    }));
                  }
                  // const otherIndex = index === 0 ? 1 : 0;
                  // const otherType = entities[otherIndex]?.type || "";
                  // const allowedTypesForThisSelect =
                  //   getAllowedTypesGivenOther(otherType);
                  const isDept = entity.type === "department";
                  const isLoading = isDept && isLoadingDepartments;
                  const firstType = entities[0]?.type || "";
                  const optionsForThisTypeSelect =
                    index === 0
                      ? entityTypes // all options for Entity 1
                      : entityTypes.filter(
                          (t) => !firstType || isAllowedPair(t.value, firstType)
                        );
                  return (
                    <div
                      key={entity.id ?? index}
                      className="entity-selector-card"
                    >
                      <div className="entity-card-header">
                        <span className="entity-number">
                          Entity {index + 1}
                        </span>
                      </div>

                      <div className="entity-form">
                        <div className="form-group">
                          <label>Entity Type</label>
                          {/* <select
                            value={entity.type ?? ""}
                            onChange={(e) =>
                              updateEntity(index, "type", e.target.value)
                            }
                            className="entity-select"
                          >
                            <option value="">Select type...</option>
                            {entityTypes.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select> */}
                          <select
                            value={entity.type ?? ""}
                            onChange={(e) =>
                              updateEntity(index, "type", e.target.value)
                            }
                            className="entity-select"
                            disabled={index === 1 && !firstType} // optional: lock Entity 2 until Entity 1 chosen
                          >
                            <option value="">Select type...</option>
                            {optionsForThisTypeSelect.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {entity.type && (
                          <div className="form-group">
                            <label>
                              Select{" "}
                              {
                                entityTypes.find((t) => t.value === entity.type)
                                  ?.label
                              }
                            </label>
                            <select
                              value={entity.name ?? ""} // keep as string id
                              onChange={(e) =>
                                updateEntity(index, "name", e.target.value)
                              }
                              className="entity-select"
                              disabled={isLoading}
                            >
                              <option value="">
                                {isLoading
                                  ? "Loading departments..."
                                  : `Select ${entity.type}...`}
                              </option>

                              {options.length === 0 && !isLoading ? (
                                <option value="" disabled>
                                  {isDept
                                    ? "No departments found"
                                    : `No ${entity.type} options`}
                                </option>
                              ) : (
                                options.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))
                              )}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/************ Analysis Type Selection ************/}
              {showAnalysisQuestions && (
                <div className="analysis-questions-section-unique">
                  <div className="questions-header-unique">
                    <HelpCircle size={24} />
                    <h3>What type of analysis would you like to perform?</h3>
                    <p>Select the analysis that best matches your needs</p>
                  </div>

                  <div className="questions-grid-unique">
                    {getDynamicAnalysisQuestions().map((question) => {
                      const IconComponent = question.icon;
                      return (
                        <div
                          key={question.id}
                          className={`question-card-unique ${
                            selectedAnalysisType === question.id
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => {
                            setComparisonResult(null);
                            setSelectedAnalysisType(question.id);
                          }}
                        >
                          <div className="question-icon-unique">
                            <IconComponent size={24} />
                          </div>
                          <div className="question-content-unique">
                            <h4>{question.title}</h4>
                            <p>{question.description}</p>
                          </div>
                          <div className="question-selector-unique">
                            {selectedAnalysisType === question.id ? (
                              <CheckCircle
                                size={20}
                                className="selected-icon"
                              />
                            ) : (
                              <div className="unselected-circle"></div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* People Selection Section */}
          {activeComparisonTab === "people" && (
            <div className="people-selection-section-unique">
              <div className="section-header">
                <h2>Compare People Based on Entity</h2>
              </div>

              <div className="entity-people-selection-unique">
                <div className="entity-type-selection-unique">
                  <div className="entity-type-selector-unique">
                    <label>Select Entity Type</label>
                    <select
                      value={selectedEntityType}
                      onChange={(e) => handleEntityTypeChange(e.target.value)}
                      className="entity-select-unique"
                    >
                      <option value="">Choose entity type...</option>
                      {peopleEntityTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedEntityType && (
                    <div className="entity-name-selector-unique">
                      <label>
                        Select Specific{" "}
                        {selectedEntityType.charAt(0).toUpperCase() +
                          selectedEntityType.slice(1)}
                      </label>
                      <select
                        value={selectedEntityName}
                        onChange={(e) => handleEntityNameChange(e.target.value)}
                        className="entity-select-unique"
                      >
                        <option value="">Choose {selectedEntityType}...</option>
                        {getAvailableEntityNames().map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {selectedEntityType && selectedEntityName && (
                  <div className="people-grid-unique">
                    <div className="people-selection-header-unique">
                      <h3>
                        Select People ({selectedPeople.length} selected from{" "}
                        {getFilteredPeople().length} total)
                      </h3>

                      {/* Search and Filters */}
                      <div className="people-search-filters-unique">
                        <div className="filters-row-unique">
                          <div className="search-bar-unique">
                            <input
                              type="text"
                              placeholder="Search by name, role, or skills..."
                              value={searchTerm}
                              onChange={(e) => {
                                setSearchTerm(e.target.value);
                                resetPagination();
                              }}
                              className="search-input-unique"
                            />
                          </div>
                          <select
                            value={selectedFilters.role}
                            onChange={(e) =>
                              handleFilterChange("role", e.target.value)
                            }
                            className="filter-select-unique"
                          >
                            <option value="">All Roles</option>
                            <option value="Senior Developer">
                              Senior Developer
                            </option>
                            <option value="UX Designer">UX Designer</option>
                            <option value="Product Manager">
                              Product Manager
                            </option>
                            <option value="Marketing Specialist">
                              Marketing Specialist
                            </option>
                          </select>
                          <select
                            value={selectedFilters.performanceRange}
                            onChange={(e) =>
                              handleFilterChange(
                                "performanceRange",
                                e.target.value
                              )
                            }
                            className="filter-select-unique"
                          >
                            <option value="">All Performance</option>
                            <option value="90-100">90-100%</option>
                            <option value="80-89">80-89%</option>
                            <option value="70-79">70-79%</option>
                          </select>
                          <select
                            value={selectedFilters.experience}
                            onChange={(e) =>
                              handleFilterChange("experience", e.target.value)
                            }
                            className="filter-select-unique"
                          >
                            <option value="">All Experience</option>
                            <option value="0-2">0-2 years</option>
                            <option value="3-5">3-5 years</option>
                            <option value="6+">6+ years</option>
                          </select>
                          <button
                            className="clear-filters-btn-unique"
                            onClick={clearAllFilters}
                          >
                            <X size={16} />
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* People Cards */}
                    <div className="people-cards-grid-unique">
                      {getPaginatedPeople().map((person) => (
                        <div
                          key={person.id}
                          className={`person-card-unique ${
                            selectedPeople.some((p) => p.id === person.id)
                              ? "selected"
                              : ""
                          } ${
                            isPersonRecommended(person) ? "recommended" : ""
                          }`}
                          onClick={() => handlePersonSelection(person)}
                        >
                          <div className="person-card-header-unique">
                            <div className="person-info-unique">
                              <h4>{person.name}</h4>
                              <p>{person.role}</p>
                            </div>
                            <div className="person-performance-unique">
                              <span className="performance-score-unique">
                                {person.performance}%
                              </span>
                            </div>
                          </div>
                          <div className="person-details-unique">
                            <div className="person-experience-unique">
                              <span className="detail-label-unique">
                                Experience:
                              </span>
                              <span className="detail-value-unique">
                                {person.experience}
                              </span>
                            </div>
                            <div className="person-skills-unique">
                              <span className="detail-label-unique">
                                Skills:
                              </span>
                              <div className="skills-tags-unique">
                                {person.skills
                                  .slice(0, 2)
                                  .map((skill, index) => (
                                    <span
                                      key={index}
                                      className="skill-tag-unique"
                                    >
                                      {skill}
                                    </span>
                                  ))}
                                {person.skills.length > 2 && (
                                  <span className="skill-tag-unique more-skills-unique">
                                    +{person.skills.length - 2}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {isPersonRecommended(person) && (
                            <div className="recommended-badge-unique">
                              <Award size={14} />
                              Recommended
                            </div>
                          )}
                          {selectedPeople.some((p) => p.id === person.id) && (
                            <div className="selected-indicator-unique">
                              <div className="checkmark-unique">✓</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {getTotalPages() > 1 && (
                      <div className="pagination-unique">
                        <button
                          className="pagination-btn-unique"
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft size={16} />
                          Previous
                        </button>
                        <div className="pagination-info-unique">
                          Page {currentPage} of {getTotalPages()}
                        </div>
                        <button
                          className="pagination-btn-unique"
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(prev + 1, getTotalPages())
                            )
                          }
                          disabled={currentPage === getTotalPages()}
                        >
                          Next
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Analysis Type Selection for People */}
                {showAnalysisQuestions && activeComparisonTab === "people" && (
                  <div className="analysis-questions-section-unique">
                    <div className="questions-header-unique">
                      <HelpCircle size={24} />
                      <h3>What type of analysis would you like to perform?</h3>
                      <p>
                        Select the analysis that best matches your needs for the
                        selected people
                      </p>
                    </div>

                    {/* <div className="questions-grid-unique">
                      {getDynamicAnalysisQuestions().map((question) => {
                        const IconComponent = question.icon;
                        const isSelected = selectedAnalysisType === question.id;

                        const handleSelect = () => {
                          // 1) Set the selected analysis tab
                          setSelectedAnalysisType(question.id);

                          // 2) Try cache first for the current selection
                          //    (expects: getCurrentCompareKey, comparisonCacheRef, generatedOnceRef in scope)
                          const key = getCurrentCompareKey();

                          if (comparisonCacheRef.current.has(key)) {
                            // Reuse cached data; prevent the effect from re-calling the API
                            setComparisonResult(
                              comparisonCacheRef.current.get(key)
                            );
                            generatedOnceRef.current.entities = true;
                          } else {
                            // No cache yet → allow the effect to call API once
                            setComparisonResult(null);
                            generatedOnceRef.current.entities = false;
                          }
                        };

                        return (
                          <div
                            key={question.id}
                            className={`question-card-unique ${
                              isSelected ? "selected" : ""
                            }`}
                            onClick={handleSelect}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                handleSelect();
                            }}
                          >
                            <div className="question-icon-unique">
                              <IconComponent size={24} />
                            </div>
                            <div className="question-content-unique">
                              <h4>{question.title}</h4>
                              <p>{question.description}</p>
                            </div>
                            <div className="question-selector-unique">
                              {isSelected ? (
                                <CheckCircle
                                  size={20}
                                  className="selected-icon"
                                />
                              ) : (
                                <div className="unselected-circle"></div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div> */}
                    <div className="questions-grid-unique">
                      {getDynamicAnalysisQuestions().map((question) => {
                        const IconComponent = question.icon;
                        // const handleSelect = () => {
                        //   // 1) Switch tab selection
                        //   setSelectedAnalysisType(question.id);
                        //   // 2) Reuse cached data for the same entity pair (no API call)
                        //   const key = getCurrentCompareKey();
                        //   if (comparisonCacheRef.current.has(key)) {
                        //     setComparisonResult(
                        //       comparisonCacheRef.current.get(key)
                        //     );
                        //     generatedOnceRef.current.entities = true;
                        //   } else {
                        //     // allow effect to call API once for this pair
                        //     generatedOnceRef.current.entities = false;
                        //   }
                        // };

                        const handleSelect = () => {
                          setSelectedAnalysisType(question.id);
                          const key = getCurrentCompareKey(); // NOTE: pair-only key (no analysis type)
                          if (comparisonCacheRef.current.has(key)) {
                            setComparisonResult(
                              comparisonCacheRef.current.get(key)
                            );
                            generatedOnceRef.current.entities = true;
                          } else {
                            // allow effect to call API once after user picks a card
                            generatedOnceRef.current.entities = false;
                          }
                        };
                        return (
                          <div
                            key={question.id}
                            className={`question-card-unique ${
                              selectedAnalysisType === question.id
                                ? "selected"
                                : ""
                            }`}
                          >
                            <div className="question-icon-unique">
                              <IconComponent size={24} />
                            </div>
                            <div className="question-content-unique">
                              <h4>{question.title}</h4>
                              <p>{question.description}</p>
                            </div>
                            <div className="question-selector-unique">
                              {selectedAnalysisType === question.id ? (
                                <CheckCircle
                                  size={20}
                                  className="selected-icon"
                                />
                              ) : (
                                <div className="unselected-circle"></div>
                              )}
                            </div>
                            onKeyDown=
                            {(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                handleSelect();
                            }}
                            <div className="question-icon-unique">
                              <IconComponent size={24} />
                            </div>
                            <div className="question-content-unique">
                              <h4>{question.title}</h4>
                              <p>{question.description}</p>
                            </div>
                            <div className="question-selector-unique">
                              {selectedAnalysisType === question.id ? (
                                <CheckCircle
                                  size={20}
                                  className="selected-icon"
                                />
                              ) : (
                                <div className="unselected-circle"></div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generate Comparison Button */}
          {showAnalysisQuestions && selectedAnalysisType && (
            <div className="comparison-actions">
              {isComparing ? (
                <button
                  disabled={isComparing}
                  className="compare-btn"
                  // onClick={generateComparison}
                  style={{ marginBottom: "30px" }}
                >
                  {isComparing ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Generating Analysis...
                    </>
                  ) : (
                    <>
                      <BarChart3 size={16} />
                      Generate Comparison Report
                    </>
                  )}
                </button>
              ) : null}
            </div>
          )}

          {/* Comparison Results */}
          {/* {comparisonResult && ( */}
          {comparisonResult &&
            selectedAnalysisType &&
            showAnalysisQuestions && (
              <div className="comparison-results-section">
                <div className="results-header">
                  <h2>
                    Entity Comparison Results
                    {/* {comparisonResult.type === "entities"
                    ? "Entity Comparison Results"
                    : `People Comparison Results - ${comparisonResult.selectedEntityName}`} */}
                  </h2>
                  <div className="results-actions">
                    <button
                      className="results-action-btn"
                      onClick={handleDetailedComparison}
                    >
                      <Eye size={16} />
                      Detailed View
                    </button>
                    {/* <button className="results-action-btn">
                    <Download size={16} />
                    Export
                  </button> */}
                  </div>
                </div>

                {/* Render the appropriate report */}
                {renderReport()}
              </div>
            )}
        </div>
        {comparisonResult && (
          <div className="comparison-actions">
            <button className="compare-btn" onClick={handleDetailedComparison}>
              <BarChart3 size={16} />
              Get Detailed Report
            </button>
          </div>
        )}
      </main>

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />

    </div>
  );
};

export default AnalysisComparisonPage;
